import { computed, inject, ref } from 'vue';
import { generateProductSlug, getCategorySlugFromProduct, slugifySegment } from '../utils/productSlug';
const DEFAULT_PRODUCT_LIMIT = 6;
const DEFAULT_CATEGORY_LIMIT = 5;
const searchIndexCache = ref(null);
const indexLoading = ref(false);
const indexError = ref(null);
let pendingIndexPromise = null;
export function useProductSearch() {
    const apiClient = inject('apiClient');
    if (!apiClient) {
        throw new Error('[useProductSearch] RestaurantApiClient was not provided. Please provide it via app.provide.');
    }
    const ensureIndex = async () => {
        if (searchIndexCache.value) {
            return;
        }
        if (pendingIndexPromise) {
            return pendingIndexPromise;
        }
        indexLoading.value = true;
        pendingIndexPromise = (async () => {
            try {
                const response = await apiClient.getMenuBundle();
                if (response.status !== 'success' || !response.data) {
                    throw new Error(response.message || 'Menu bundle could not be loaded');
                }
                searchIndexCache.value = buildSearchIndex(response.data);
                indexError.value = null;
            }
            catch (error) {
                indexError.value = error?.message || 'Unbekannter Fehler beim Laden des Suchindex';
                throw error;
            }
            finally {
                indexLoading.value = false;
                pendingIndexPromise = null;
            }
        })();
        return pendingIndexPromise;
    };
    const search = async (query, options = {}) => {
        const normalized = query.trim().toLowerCase();
        if (!normalized) {
            return {
                products: [],
                categories: [],
                keywords: []
            };
        }
        await ensureIndex();
        if (!searchIndexCache.value) {
            throw new Error(indexError.value || 'Suchindex nicht verfügbar');
        }
        return runScoredSearch(normalized, searchIndexCache.value, options);
    };
    return {
        search,
        ensureIndex,
        isIndexLoading: computed(() => indexLoading.value),
        lastError: computed(() => indexError.value)
    };
}
function buildSearchIndex(bundle) {
    const rawProducts = Array.isArray(bundle?.products) ? bundle.products : [];
    const rawCategories = Array.isArray(bundle?.categories) ? bundle.categories : [];
    const products = rawProducts
        .map((item, index) => buildProductRecord(item, index))
        .filter((record) => record !== null);
    const categories = buildCategoryRecords(rawCategories, products);
    return {
        products,
        categories
    };
}
function buildProductRecord(item, fallbackIndex) {
    const raw = item || {};
    const id = String(raw._id ?? raw.id ?? '');
    if (!id) {
        return null;
    }
    const name = typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : `Produkt ${fallbackIndex + 1}`;
    const slug = generateProductSlug(raw) || slugifySegment(name) || id;
    let categorySlug = '';
    let categoryName = '';
    let categoryId;
    const inferredCategorySlug = getCategorySlugFromProduct(raw);
    if (inferredCategorySlug) {
        categorySlug = slugifySegment(inferredCategorySlug);
    }
    if (!categorySlug && raw.category && typeof raw.category === 'object') {
        if (raw.category.slug) {
            categorySlug = slugifySegment(raw.category.slug);
        }
        else if (raw.category.name) {
            categorySlug = slugifySegment(raw.category.name);
        }
        categoryId = String(raw.category._id ?? raw.category.id ?? raw.category.slug ?? raw.category.name ?? '') || undefined;
    }
    else if (!categorySlug && typeof raw.category === 'string') {
        categorySlug = slugifySegment(raw.category);
        categoryId = raw.category;
    }
    if (!categorySlug && typeof raw.categoryName === 'string') {
        categorySlug = slugifySegment(raw.categoryName);
    }
    categorySlug = categorySlug || 'produkte';
    categoryName = raw.categoryName || raw.category?.name || raw.category || 'Produkte';
    const brand = typeof raw.brand === 'string' ? raw.brand : (typeof raw.manufacturer === 'string' ? raw.manufacturer : raw.producer ?? null);
    const keywords = collectKeywords(raw);
    const keywordsNormalized = keywords.map((keyword) => keyword.toLowerCase());
    const description = resolveDescription(raw);
    const priceCent = resolvePriceCent(raw);
    const searchText = [
        name,
        description,
        brand ?? '',
        categoryName,
        keywords.join(' '),
        Array.isArray(raw.tags) ? raw.tags.join(' ') : ''
    ]
        .join(' ')
        .toLowerCase();
    const imageUrl = resolveImageUrl(raw);
    const available = raw.available !== false && raw.isVisible !== false && raw.isDeleted !== true;
    return {
        id,
        slug,
        categoryId,
        categorySlug,
        categoryName,
        name,
        description,
        brand,
        keywords,
        keywordsNormalized,
        searchText,
        priceCent,
        imageUrl,
        available
    };
}
function buildCategoryRecords(rawCategories, products) {
    const categories = [];
    const seenSlugs = new Set();
    const productsBySlug = new Map();
    products.forEach((product) => {
        const slug = product.categorySlug || 'produkte';
        if (!productsBySlug.has(slug)) {
            productsBySlug.set(slug, []);
        }
        productsBySlug.get(slug).push(product);
    });
    rawCategories.forEach((category, index) => {
        const slug = slugifySegment(category.slug || category._id || category.name || `category-${index}`);
        const relatedProducts = productsBySlug.get(slug) || [];
        const keywords = buildCategoryKeywords(category, relatedProducts);
        categories.push({
            id: String(category._id ?? slug ?? index),
            slug,
            name: category.name || formatCategoryLabel(slug),
            description: typeof category.description === 'string' ? category.description : '',
            keywords,
            keywordsNormalized: keywords.map((keyword) => keyword.toLowerCase()),
            productCount: relatedProducts.length || Number(category.productCount) || 0
        });
        seenSlugs.add(slug);
    });
    productsBySlug.forEach((relatedProducts, slug) => {
        if (seenSlugs.has(slug)) {
            return;
        }
        const keywords = buildCategoryKeywords(null, relatedProducts);
        categories.push({
            id: slug,
            slug,
            name: relatedProducts[0]?.categoryName || formatCategoryLabel(slug),
            description: '',
            keywords,
            keywordsNormalized: keywords.map((keyword) => keyword.toLowerCase()),
            productCount: relatedProducts.length
        });
    });
    return categories;
}
function runScoredSearch(query, index, options = {}) {
    const tokens = query.split(/\s+/).filter(Boolean);
    if (!tokens.length) {
        return { products: [], categories: [], keywords: [] };
    }
    const minScore = options.minScore ?? 1;
    const productLimit = options.productLimit ?? DEFAULT_PRODUCT_LIMIT;
    const categoryLimit = options.categoryLimit ?? DEFAULT_CATEGORY_LIMIT;
    const productHits = index.products
        .map((record) => {
        const { score, matchedKeywords } = scoreProduct(record, tokens);
        return score >= minScore ? { ...record, score, matchedKeywords } : null;
    })
        .filter((hit) => Boolean(hit))
        .sort((a, b) => b.score - a.score)
        .slice(0, productLimit);
    const keywordSet = new Set();
    productHits.forEach((hit) => {
        hit.matchedKeywords.forEach((keyword) => keywordSet.add(keyword));
    });
    const categoryHits = index.categories
        .map((record) => {
        const { score, sampleProducts } = scoreCategory(record, tokens, productHits);
        return score >= minScore / 2 ? { ...record, score, sampleProducts } : null;
    })
        .filter((hit) => Boolean(hit))
        .sort((a, b) => {
        if (b.score !== a.score) {
            return b.score - a.score;
        }
        return (b.productCount || 0) - (a.productCount || 0);
    })
        .slice(0, categoryLimit);
    return {
        products: productHits,
        categories: categoryHits,
        keywords: Array.from(keywordSet).slice(0, 8)
    };
}
function scoreProduct(record, terms) {
    let score = 0;
    const matchedKeywords = new Set();
    const nameLower = record.name.toLowerCase();
    const brandLower = record.brand ? record.brand.toLowerCase() : '';
    const categoryLower = record.categoryName.toLowerCase();
    terms.forEach((term) => {
        if (!term) {
            return;
        }
        if (nameLower.includes(term)) {
            score += 6;
        }
        if (brandLower && brandLower.includes(term)) {
            score += 3;
        }
        if (categoryLower.includes(term)) {
            score += 2;
        }
        if (record.searchText.includes(term)) {
            score += 1;
        }
        record.keywordsNormalized.forEach((keyword, index) => {
            if (keyword.includes(term)) {
                score += 2;
                matchedKeywords.add(record.keywords[index]);
            }
        });
    });
    if (!record.available) {
        score *= 0.8;
    }
    return {
        score,
        matchedKeywords: Array.from(matchedKeywords)
    };
}
function scoreCategory(record, terms, productHits) {
    let score = 0;
    const nameLower = record.name.toLowerCase();
    const descriptionLower = record.description.toLowerCase();
    terms.forEach((term) => {
        if (!term) {
            return;
        }
        if (nameLower.includes(term)) {
            score += 4;
        }
        if (descriptionLower.includes(term)) {
            score += 1.5;
        }
        if (record.keywordsNormalized.some((keyword) => keyword.includes(term))) {
            score += 2;
        }
    });
    const relatedProducts = productHits.filter((product) => product.categorySlug === record.slug);
    if (relatedProducts.length) {
        score += relatedProducts.length * 1.5;
    }
    return {
        score,
        sampleProducts: relatedProducts.slice(0, 3).map((product) => product.name)
    };
}
function collectKeywords(raw) {
    const keywords = new Set();
    if (Array.isArray(raw.keywords)) {
        raw.keywords.forEach((keyword) => {
            const value = String(keyword).trim();
            if (value) {
                keywords.add(value);
            }
        });
    }
    if (Array.isArray(raw.tags)) {
        raw.tags.forEach((tag) => {
            const value = String(tag).trim();
            if (value) {
                keywords.add(value);
            }
        });
    }
    if (typeof raw.brand === 'string' && raw.brand.trim()) {
        keywords.add(raw.brand.trim());
    }
    if (typeof raw.categoryName === 'string' && raw.categoryName.trim()) {
        keywords.add(raw.categoryName.trim());
    }
    return Array.from(keywords);
}
function resolveDescription(raw) {
    if (typeof raw.shortDescription === 'string' && raw.shortDescription.trim()) {
        return raw.shortDescription.trim();
    }
    if (typeof raw.description === 'string' && raw.description.trim()) {
        return raw.description.trim();
    }
    return '';
}
function resolvePriceCent(raw) {
    if (typeof raw.priceCent === 'number') {
        return raw.priceCent;
    }
    if (typeof raw.price === 'number') {
        return Math.round(raw.price * 100);
    }
    return 0;
}
function resolveImageUrl(raw) {
    const imageData = raw.imageData;
    if (imageData?.baseUrl) {
        const type = imageData.type || 'webp';
        return `${imageData.baseUrl}_md_256x256.${type}`;
    }
    if (Array.isArray(raw.images) && raw.images.length > 0) {
        const firstImage = raw.images[0];
        if (typeof firstImage === 'string' && firstImage.trim()) {
            return firstImage.trim();
        }
    }
    if (typeof raw.image === 'string' && raw.image.trim()) {
        return raw.image.trim();
    }
    if (typeof raw.imageUrl === 'string' && raw.imageUrl.trim()) {
        return raw.imageUrl.trim();
    }
    return null;
}
function buildCategoryKeywords(category, products) {
    const keywords = new Set();
    if (category) {
        if (Array.isArray(category.keywords)) {
            category.keywords.forEach((keyword) => {
                const value = String(keyword).trim();
                if (value) {
                    keywords.add(value);
                }
            });
        }
        if (typeof category.name === 'string') {
            keywords.add(category.name);
        }
        if (typeof category.description === 'string') {
            extractKeywordCandidates(category.description).forEach((keyword) => keywords.add(keyword));
        }
    }
    products.forEach((product) => {
        keywords.add(product.categoryName);
        if (product.brand) {
            keywords.add(product.brand);
        }
        product.keywords.forEach((keyword) => keywords.add(keyword));
    });
    return Array.from(keywords).filter(Boolean).slice(0, 12);
}
function extractKeywordCandidates(text) {
    return text
        .split(/[\s,;:.!\-_/]+/)
        .map((segment) => segment.trim())
        .filter((segment) => segment.length >= 3);
}
function formatCategoryLabel(slug) {
    return slug
        .split(/[-_]/)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');
}
